import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { Autocomplete } from '@shopify/polaris';

const propTypes = {
  options: PropTypes.array.isRequired, // [{ label, value }], same shape a Select takes
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired, // called (value, id), like Polaris Select, so handlers port over
  id: PropTypes.string,

  label: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
}

const defaultProps = {
  value: '',
  id: undefined,
  label: '',
  placeholder: 'Escribí para buscar',
  disabled: false,
  error: undefined,
}

// A Select you can type into, for lists long enough that scanning them with the mouse is the slow
// part - couriers, teams. Filtering happens here because the options are already loaded; the shared
// Autocomplete in this folder is the other case, where the list lives on the server.
//
// Keep using Select for short lists: with four options, typing is slower than picking.
const SearchableSelect = (props) => {
  const { options, value, onChange, id, label, placeholder, disabled, error } = props;

  const labelFor = (optionValue) => options.find((option) => option.value === optionValue)?.label ?? '';
  // What the box shows and what filters the list are kept apart, so resetting the text on blur
  // cannot reshuffle the list between a tap and its click.
  const [text, setText] = useState(labelFor(value));
  const [query, setQuery] = useState('');

  // The selected value can change from outside (a form reset, a dependent field reloading its
  // options), and the text box has to follow it or it would show a label nobody selected.
  useEffect(() => { setText(labelFor(value)); setQuery(''); }, [value, options]);

  // Copies, not the caller's objects: Polaris writes an index-based `id` into every option it
  // receives, and sharing objects between lists left React rendering rows under duplicate keys.
  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    const matches = term ? options.filter((option) => String(option.label).toLowerCase().includes(term)) : options;

    return matches.map((option) => ({ label: option.label, value: option.value }));
  }, [options, query]);

  return (
    <Autocomplete
      options={filtered}
      selected={value ? [value] : []}
      onSelect={(selected) => {
        onChange(selected[0] ?? '', id);
        setText(labelFor(selected[0]));
        setQuery('');
      }}
      textField={
        <Autocomplete.TextField
          id={id}
          label={label}
          value={text}
          onChange={(next) => { setText(next); setQuery(next); }}
          onFocus={() => setQuery('')}
          // Leaving without picking puts the selected label back, so the text never names a
          // courier the form does not hold.
          onBlur={() => setText(labelFor(value))}
          placeholder={placeholder}
          disabled={disabled}
          error={error}
          autoComplete="off"
        />
      }
    />
  );
};

SearchableSelect.propTypes = propTypes;
SearchableSelect.defaultProps = defaultProps;

export default SearchableSelect;
